import React from 'react';
import { X, DollarSign, Calendar, ShoppingBag, Users, Wifi, WifiOff } from 'lucide-react';

/**
 * Real-time notifications component
 * Shows live updates for revenues, bookings, expenses
 */
export const RealtimeNotifications = ({ notifications, onDismiss }) => {
  if (!notifications || notifications.length === 0) return null;

  const getIcon = (type) => {
    switch (type) {
      case 'revenue':
        return <DollarSign className="w-5 h-5 text-green-400" />;
      case 'booking':
        return <Calendar className="w-5 h-5 text-blue-400" />;
      case 'expense':
        return <ShoppingBag className="w-5 h-5 text-red-400" />;
      default:
        return <Users className="w-5 h-5 text-gray-400" />;
    }
  };

  const getBgColor = (type) => {
    switch (type) {
      case 'revenue':
        return 'bg-green-900/50 border-green-700';
      case 'booking':
        return 'bg-blue-900/50 border-blue-700';
      case 'expense':
        return 'bg-red-900/50 border-red-700';
      default:
        return 'bg-gray-900/50 border-gray-700';
    }
  };

  return (
    <div className="fixed bottom-4 left-4 z-50 flex flex-col gap-2 max-w-sm" dir="rtl">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`${getBgColor(notification.type)} border rounded-lg p-3 shadow-lg 
                     animate-slide-in-left backdrop-blur-sm flex items-start gap-3`}
        >
          <div className="flex-shrink-0 mt-0.5">
            {getIcon(notification.type)}
          </div>
          <div className="flex-grow min-w-0">
            <p className="text-sm font-medium text-white">{notification.title}</p>
            <p className="text-xs text-gray-300 truncate">{notification.message}</p>
          </div>
          <button
            onClick={() => onDismiss(notification.id)}
            className="flex-shrink-0 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
};

/**
 * Connection status indicator
 */
export const ConnectionStatus = ({ isConnected }) => {
  return (
    <div 
      className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs
                  ${isConnected ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}
      title={isConnected ? 'متصل' : 'غير متصل'}
    >
      {isConnected ? (
        <>
          <Wifi className="w-3 h-3" />
          <span className="hidden sm:inline">مباشر</span>
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
        </>
      ) : (
        <>
          <WifiOff className="w-3 h-3" />
          <span className="hidden sm:inline">غير متصل</span>
        </>
      )}
    </div>
  );
};

/**
 * Online users indicator for admin
 */
export const OnlineUsersIndicator = ({ onlineUsers, count }) => {
  const [showList, setShowList] = React.useState(false);
  
  return (
    <div className="relative">
      <button
        onClick={() => setShowList(!showList)}
        className="flex items-center gap-2 px-3 py-1.5 bg-[#2a2a2a] rounded-lg 
                   hover:bg-[#333] transition-colors"
      >
        <Users className="w-4 h-4 text-[#D4AF37]" />
        <span className="text-sm text-white">{count?.total || 0}</span>
        <span className="text-xs text-gray-400">متصل</span>
      </button>
      
      {showList && onlineUsers && onlineUsers.length > 0 && (
        <div 
          className="absolute top-full right-0 mt-2 w-56 bg-[#2a2a2a] rounded-lg 
                     shadow-xl border border-[#444] z-50"
          dir="rtl"
        >
          <div className="p-2 border-b border-[#444]">
            <p className="text-xs text-gray-400">المستخدمون المتصلون</p>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {onlineUsers.map((user, idx) => (
              <div 
                key={user.user_id || idx}
                className="flex items-center gap-2 px-3 py-2 hover:bg-[#333]"
              >
                <span className={`w-2 h-2 rounded-full ${
                  user.role === 'admin' ? 'bg-[#D4AF37]' : 'bg-green-400'
                }`} />
                <span className="text-sm text-white flex-grow">{user.name}</span>
                <span className="text-xs text-gray-500">
                  {user.role === 'admin' ? 'مدير' : 'حلاق'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Real-time stats card for dashboard
 */
export const RealtimeStatsCard = ({ label, value, icon: Icon, color, isLive }) => {
  return (
    <div className="bg-[#2a2a2a] rounded-xl p-4 relative overflow-hidden">
      {isLive && (
        <div className="absolute top-2 left-2">
          <span className="flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm mb-1">{label}</p>
          <p className={`text-2xl font-bold ${color || 'text-white'}`}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        </div>
        {Icon && (
          <div className={`p-3 rounded-lg ${color ? 'bg-opacity-20' : 'bg-[#333]'}`}
               style={{ backgroundColor: color ? `${color}20` : undefined }}>
            <Icon className={`w-6 h-6 ${color || 'text-[#D4AF37]'}`} />
          </div>
        )}
      </div>
    </div>
  );
};

// Add CSS animation
const style = document.createElement('style');
style.textContent = `
  @keyframes slide-in-left {
    from {
      transform: translateX(-100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  .animate-slide-in-left {
    animation: slide-in-left 0.3s ease-out;
  }
`;
if (!document.querySelector('style[data-realtime]')) {
  style.setAttribute('data-realtime', 'true');
  document.head.appendChild(style);
}

export default RealtimeNotifications;
